
import React from 'react';
import { motion } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import GlassPill from '@/components/ui/GlassPill';

interface ResearchCardSkeletonProps {
  index?: number;
}

const ResearchCardSkeleton: React.FC<ResearchCardSkeletonProps> = ({ index = 0 }) => {
  return (
    <motion.div 
      className="group relative cursor-pointer transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-xl hover:shadow-purple-100/30 dark:hover:shadow-purple-900/20"
      initial={{ opacity: 0 }}
      animate={{ 
        opacity: 1,
        transition: {
          duration: 0.3,
          delay: index * 0.02
        }
      }}
      style={{ 
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        willChange: 'transform',
        transformStyle: 'preserve-3d'
      }}
    >
      {/* Modern card with glass morphism effect - matching exact structure */}
      <div className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 rounded-2xl p-6 h-full shadow-lg group-hover:border-purple-200/50 dark:group-hover:border-purple-700/50 transition-all duration-300">
        {/* Top row: Category and Favorite button */}
        <div className="flex items-center justify-between mb-4">
          {/* Category pill skeleton */}
          <div className="rounded-full font-medium backdrop-blur-sm border shadow-sm transition-all duration-300 font-sans bg-blue-100/70 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/30 text-blue-700 dark:text-blue-300 text-xs font-medium px-3 py-1">
            <Skeleton className="h-3 w-16" />
          </div>
          
          {/* Favorite button skeleton */}
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
        
        {/* Title skeleton - matching the real title styling */}
        <div className="mb-3">
          <Skeleton className="h-6 w-3/4 mb-1" />
          <Skeleton className="h-6 w-1/2" />
        </div>
        
        {/* Content snippet skeleton - matching the real content area */}
        <div className="mb-4 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        
        {/* Bottom row: Sources info and date - matching exact layout */}
        <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
          <div className="flex items-center gap-2">
            {/* Sources info skeleton */}
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
          
          {/* Date skeleton */}
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ResearchCardSkeleton;
